import {
  buildStatusNoticeExecutionSummaryItems,
  formatDiagnosticDurationLabel,
} from "./resultsChromeData.js";
import { sanitizeOptimizationProgressForStatus } from "./optimizationProgress.js";
import { buildStatusNoticeAlertCaptureContext } from "../lib/dispatchAlertCaptureContext.js";
import { readCurrentDispatchDate, readCurrentDispatchPeriod } from "./dispatchDateUrls.js";

export function formatOptimizationStatusTime(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  const resolvedDate = new Date(value);
  if (Number.isNaN(resolvedDate.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("fr-CA", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(resolvedDate);
}

export function formatOptimizationDispatchLabel(dispatchDate, _dispatchPeriod = null) {
  const rawDate = typeof dispatchDate === "string" ? dispatchDate.trim() : "";
  if (!rawDate) {
    return "";
  }

  const parsedDate = new Date(`${rawDate.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawDate;
  }

  return new Intl.DateTimeFormat("fr-CA", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsedDate);
}

export function appendOptimizationDispatchLabel(baseTitle, dispatchDate, dispatchPeriod) {
  const normalizedTitle = typeof baseTitle === "string" ? baseTitle.trim() : "";
  const dispatchLabel = formatOptimizationDispatchLabel(dispatchDate, dispatchPeriod);
  if (!normalizedTitle) {
    return dispatchLabel;
  }
  if (!dispatchLabel) {
    return normalizedTitle;
  }
  if (normalizedTitle.includes(dispatchLabel)) {
    return normalizedTitle;
  }

  return `${normalizedTitle} — ${dispatchLabel}`;
}

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveOptimizationDiagnostics(candidate) {
  return candidate && typeof candidate === "object" && !Array.isArray(candidate)
    ? candidate
    : null;
}

function buildOptimizationSolverBudgetNoticeContent(jobState) {
  const diagnostics = resolveOptimizationDiagnostics(jobState?.optimizationDiagnostics);
  if (!diagnostics) {
    return null;
  }

  const runEventDigest = resolveOptimizationDiagnostics(diagnostics.run_event_digest);
  const solverExplainer = resolveOptimizationDiagnostics(diagnostics.solver_explainer);
  const solverBreakdown = resolveOptimizationDiagnostics(diagnostics.solver_breakdown);
  const primarySlowdownSource = normalizeInlineText(runEventDigest?.primary_slowdown_source)
    || normalizeInlineText(solverExplainer?.primary_slowdown_source);
  const solverBudgetSuspectCount = Number.isFinite(Number(solverBreakdown?.ortools_group_budget_suspect_count))
    ? Math.max(0, Math.round(Number(solverBreakdown.ortools_group_budget_suspect_count)))
    : 0;
  if (primarySlowdownSource !== "solver_ortools_group_budget" && solverBudgetSuspectCount <= 0) {
    return null;
  }

  return {
    detailItems: [{
      key: "next-step",
      label: "Étape suivante",
      value: "Le calcul a manqué de temps : le résultat peut être moins bon. Relancez « Optimiser » au besoin.",
    }],
    message: normalizeInlineText(jobState?.error || jobState?.message),
  };
}

const OPTIMIZATION_GROUP_VALIDATION_HEADER_PATTERN = /^(?<file>[^:]+): (?<count>\d+) problème\(s\) métier détecté\(s\)\.\s*(?<details>.*)$/u;
const OPTIMIZATION_GROUP_VALIDATION_ENTRY_PATTERN = /\[Bon commande [^\]]+\][\s\S]*?(?=(?:\s+\[Bon commande [^\]]+\])|(?:\s+\d+ autre\(s\) problème\(s\) non affiché\(s\)\.$)|$)/gu;
const OPTIMIZATION_GROUP_VALIDATION_REMAINING_PATTERN = /\s*(?<count>\d+) autre\(s\) problème\(s\) non affiché\(s\)\.$/u;
const OPTIMIZATION_GROUP_VALIDATION_ORDER_PATTERN = /^\[Bon commande (?<orderId>[^\]]+)\]\s*(?<detail>.*)$/u;
const OPTIMIZATION_GROUP_VALIDATION_LOADING_COUNT_PATTERN = /^(?<count>\d+) ligne\(s\) de Chargement détectée\(s\) \(lignes: (?<lines>[^)]+)\)\. Une seule est requise\.$/u;
const OPTIMIZATION_GROUP_VALIDATION_NO_DELIVERY_PATTERN = /^aucun Déchargement détecté\. Au moins une ligne est requise\.$/u;
const OPTIMIZATION_GROUP_VALIDATION_FIELD_MISMATCH_PATTERN = /^(?<field>code de produit|produit) incohérent entre la ligne de Chargement (?<loadingRow>\d+) \((?<loadingValue>.*?)\) et la ligne de Déchargement (?<deliveryRow>\d+) \((?<deliveryValue>.*?)\)\.$/u;
const OPTIMIZATION_GROUP_VALIDATION_QUANTITY_PATTERN = /^quantités incohérentes: Chargement = (?<loadingValue>[^,]+), Déchargements = (?<deliveryValue>.+)\.$/u;

function buildOptimizationValidationIssueValues(values) {
  return values.reduce((items, item, index) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const label = normalizeInlineText(item.label);
    const value = normalizeInlineText(item.value);
    if (!label || !value) {
      return items;
    }

    items.push({
      key: normalizeInlineText(item.key) || `value-${index + 1}`,
      label,
      value,
    });
    return items;
  }, []);
}

function parseOptimizationValidationIssue(rawIssue, index) {
  const normalizedIssue = normalizeInlineText(rawIssue);
  const issueMatch = normalizedIssue.match(OPTIMIZATION_GROUP_VALIDATION_ORDER_PATTERN);
  if (!issueMatch?.groups) {
    return null;
  }

  const orderId = normalizeInlineText(issueMatch.groups.orderId);
  const detail = normalizeInlineText(issueMatch.groups.detail);
  if (!detail) {
    return null;
  }

  const loadingCountMatch = detail.match(OPTIMIZATION_GROUP_VALIDATION_LOADING_COUNT_PATTERN);
  if (loadingCountMatch?.groups) {
    const detectedCount = normalizeInlineText(loadingCountMatch.groups.count);
    const lineList = normalizeInlineText(loadingCountMatch.groups.lines);
    return {
      key: `validation-issue-${index + 1}`,
      orderId,
      summary: detectedCount === "1"
        ? "La commande doit contenir une seule ligne de Chargement exploitable."
        : `La commande doit contenir une seule ligne de Chargement, mais ${detectedCount} lignes ont été trouvées.`,
      title: detectedCount === "1" ? "Chargement à vérifier" : "Chargements en double",
      values: buildOptimizationValidationIssueValues([
        {
          key: "loading-lines",
          label: detectedCount === "1" ? "Ligne Chargement" : "Lignes Chargement",
          value: lineList,
        },
      ]),
    };
  }

  if (OPTIMIZATION_GROUP_VALIDATION_NO_DELIVERY_PATTERN.test(detail)) {
    return {
      key: `validation-issue-${index + 1}`,
      orderId,
      summary: "La commande doit contenir au moins une ligne de Déchargement exploitable.",
      title: "Déchargement manquant",
      values: [],
    };
  }

  const fieldMismatchMatch = detail.match(OPTIMIZATION_GROUP_VALIDATION_FIELD_MISMATCH_PATTERN);
  if (fieldMismatchMatch?.groups) {
    const field = normalizeInlineText(fieldMismatchMatch.groups.field).toLowerCase();
    const fieldLabel = field === "code de produit" ? "Code produit" : "Produit";
    return {
      key: `validation-issue-${index + 1}`,
      orderId,
      summary: `Le ${fieldLabel.toLowerCase()} du Chargement et du Déchargement ne correspond pas pour cette commande.`,
      title: `${fieldLabel} différent`,
      values: buildOptimizationValidationIssueValues([
        {
          key: "loading-value",
          label: `Chargement ligne ${normalizeInlineText(fieldMismatchMatch.groups.loadingRow)}`,
          value: normalizeInlineText(fieldMismatchMatch.groups.loadingValue) || "Valeur absente",
        },
        {
          key: "delivery-value",
          label: `Déchargement ligne ${normalizeInlineText(fieldMismatchMatch.groups.deliveryRow)}`,
          value: normalizeInlineText(fieldMismatchMatch.groups.deliveryValue) || "Valeur absente",
        },
      ]),
    };
  }

  const quantityMatch = detail.match(OPTIMIZATION_GROUP_VALIDATION_QUANTITY_PATTERN);
  if (quantityMatch?.groups) {
    return {
      key: `validation-issue-${index + 1}`,
      orderId,
      summary: "La somme des Déchargements n'est pas égale à la quantité du Chargement.",
      title: "Quantités différentes",
      values: buildOptimizationValidationIssueValues([
        {
          key: "loading-quantity",
          label: "Chargement",
          value: normalizeInlineText(quantityMatch.groups.loadingValue),
        },
        {
          key: "delivery-quantity",
          label: "Déchargements",
          value: normalizeInlineText(quantityMatch.groups.deliveryValue),
        },
      ]),
    };
  }

  return {
    key: `validation-issue-${index + 1}`,
    orderId,
    summary: "Vérifiez la commande dans le fichier source puis relancez l'optimisation.",
    title: detail,
    values: [],
  };
}

function buildOptimizationValidationNoticeContent(errorText) {
  const normalizedError = normalizeInlineText(errorText);
  const headerMatch = normalizedError.match(OPTIMIZATION_GROUP_VALIDATION_HEADER_PATTERN);
  if (!headerMatch?.groups) {
    return null;
  }

  const fileName = normalizeInlineText(headerMatch.groups.file);
  const parsedCount = Number.parseInt(normalizeInlineText(headerMatch.groups.count), 10);
  const rawDetails = normalizeInlineText(headerMatch.groups.details);
  const remainingMatch = rawDetails.match(OPTIMIZATION_GROUP_VALIDATION_REMAINING_PATTERN);
  const remainingCount = Number.parseInt(normalizeInlineText(remainingMatch?.groups?.count), 10) || 0;
  const visibleDetails = remainingMatch && Number.isInteger(remainingMatch.index)
    ? rawDetails.slice(0, remainingMatch.index).trim()
    : rawDetails;
  const issueItems = Array.from(visibleDetails.matchAll(OPTIMIZATION_GROUP_VALIDATION_ENTRY_PATTERN))
    .map((match, index) => parseOptimizationValidationIssue(match[0], index))
    .filter(Boolean);
  const issueCount = Number.isFinite(parsedCount) && parsedCount > 0
    ? parsedCount
    : (issueItems.length + remainingCount);
  const pluralSuffix = issueCount > 1 ? "s" : "";

  return {
    detailItems: [
      {
        key: "source-file",
        label: "Fichier",
        value: fileName || "Fichier source importé",
      },
      {
        key: "issue-count",
        label: `Anomalie${pluralSuffix}`,
        value: String(issueCount),
      },
      {
        key: "next-step",
        label: "Étape suivante",
        value: "Corriger les lignes importées puis relancer",
      },
    ],
    issueItems,
    message: fileName
      ? `Le fichier ${fileName} contient ${issueCount} anomalie${pluralSuffix} métier bloquante${pluralSuffix}. Corrigez les lignes signalées ci-dessous puis relancez l'optimisation.`
      : `Des anomalies métier bloquantes ont été détectées. Corrigez les lignes signalées ci-dessous puis relancez l'optimisation.`,
  };
}

function buildProtectedConstraintsPreflightNotice(details, diagnostics, action = "Optimiser") {
  const guard = diagnostics?.protected_constraints_guard;
  const violations = details?.reason_code === "protected_constraints_incompatible"
    ? details.violations : guard?.status === "preflight_rejected" ? guard.violations : null;
  if (!Array.isArray(violations) || !violations.length) return null;
  const number = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 3 });
  const tons = (value) => `${number.format(value)}\u00a0t`;
  const hours = (minutes) => {
    const remainder = minutes % 60;
    return `${Math.floor(minutes / 60)}\u00a0h${remainder ? `\u00a0${number.format(remainder)}\u00a0min` : ""}`;
  };
  const orderList = new Intl.ListFormat("fr-CA", { style: "long", type: "conjunction" });
  const causes = violations.map((item) => {
    const truck = normalizeInlineText(item?.truck_name || item?.truck_id);
    const orderIds = Array.isArray(item?.order_ids) ? item.order_ids : item?.order_id ? [item.order_id] : [];
    if (!truck || !orderIds.length) return null;
    // Chargé PM : ses compartiments sont fixés à sa création ; la remorque a changé depuis.
    if (item.constraint_scope === "end_of_day_load" && Array.isArray(item.compartments) && item.compartments.length) {
      const trailer = normalizeInlineText(item.trailer_name);
      return {
        truck, orderIds, capacity: true, endOfDayLoad: true,
        message: `Les compartiments ${trailer ? `de la remorque ${trailer}` : `du camion ${truck}`} ont changé depuis son Chargé PM : son chargement n'y tient plus.`,
        compartments: orderList.format(item.compartments.map((compartment, index) => `${compartment.position} (${tons(compartment.loaded_tons)}${index ? "" : " chargées"}${
          Number.isFinite(compartment.capacity_tons) ? ` pour ${tons(compartment.capacity_tons)}` : ", compartiment retiré"})`)),
      };
    }
    if (item.reason_code === "protected_work_minimum_exceeded"
      && Number.isFinite(item.minimum_duration_minutes) && Number.isFinite(item.limit_minutes)
      && item.minimum_duration_minutes > item.limit_minutes) {
      return {
        truck, orderIds, capacity: false,
        message: `Les tâches protégées du camion ${truck} nécessitent au minimum ${hours(item.minimum_duration_minutes)}, pour ${hours(item.limit_minutes)} autorisées${item.max_work_flex_minutes > 0 ? ` (dont ${number.format(item.max_work_flex_minutes)} min de tolérance)` : ""}.`,
      };
    }
    const quantity = item.protected_quantity_tons ?? item.order_quantity_tons;
    if (item.reason_code === "capacity_exceeded" && Number.isFinite(quantity)
      && Number.isFinite(item.capacity_tons) && quantity > item.capacity_tons) {
      const loading = normalizeInlineText(item.loading_client_name);
      const endOfDayLoad = item.constraint_scope === "end_of_day_load";
      return {
        truck, orderIds, capacity: true, endOfDayLoad,
        message: endOfDayLoad
          ? `Chargé PM trop lourd : ${truck} porte ${tons(quantity)} déjà chargées${loading ? ` (${loading})` : ""} pour une capacité de ${tons(item.capacity_tons)} ; il faut une capacité d'au moins ${tons(quantity)}.`
          : `Camion ${truck}${loading ? `, chargement ${loading}` : ""} : ${tons(quantity)} protégées pour une capacité de ${tons(item.capacity_tons)}.`,
      };
    }
    return null;
  });
  if (causes.some((item) => !item)) return null;
  causes.sort((left, right) => Number(right.capacity) - Number(left.capacity));
  const nextSteps = [
    causes.some((cause) => cause.endOfDayLoad && !cause.compartments)
      && `Retirez le Chargé PM, ou corrigez la capacité de la remorque (Data Manager › Remorques) si elle est fausse, puis relancez ${action}.`,
    causes.some((cause) => cause.compartments)
      && `Retirez le Chargé PM, ou corrigez les compartiments de la remorque (Data Manager › Remorques) s'ils sont faux, puis relancez ${action}.`,
    causes.some((cause) => !cause.endOfDayLoad)
      && `Retirez la protection des commandes concernées pour permettre leur réaffectation, puis relancez ${action}.`,
  ].filter(Boolean);
  const blocked = action === "Réinitialiser" ? "Réinitialisation bloquée" : "Optimisation bloquée";
  return {
    title: causes.length > 1 ? `${blocked} — protections à corriger`
      : causes[0].compartments ? `${blocked} — compartiments trop petits pour le Chargé PM`
        : causes[0].endOfDayLoad ? `${blocked} — Chargé PM trop lourd`
          : causes[0].capacity ? `${blocked} — chargement protégé trop lourd`
            : `${blocked} — durée de travail insuffisante`,
    statusLabel: "Action requise",
    message: causes[0].message,
    detailItems: [
      ...causes.flatMap((cause, index) => [
        ...(index ? [{ key: `constraint-${index}`, label: "Autre blocage", value: cause.message }] : []),
        ...(cause.compartments ? [{ key: `compartments-${index}`, label: "Compartiments trop petits", value: cause.compartments }] : []),
        { key: `orders-${index}`, label: "Commandes", value: orderList.format(cause.orderIds) },
      ]),
      { key: "next-step", label: "Pour relancer", value: nextSteps.join(" ") },
    ],
  };
}

function buildOptimizationConstraintNoticeContent(jobState) {
  if (jobState?.resultCommitted === true) {
    return null;
  }
  const diagnostics = resolveOptimizationDiagnostics(jobState?.optimizationDiagnostics);
  const details = resolveOptimizationDiagnostics(jobState?.errorDetails);
  if (jobState?.operationKind === "planning_reset") {
    // Seul le Chargé PM reste fixe ; la réponse de la réinitialisation porte ses détails.
    const notice = buildProtectedConstraintsPreflightNotice(details?.error_details, null, "Réinitialiser");
    return notice && { ...notice, replacesError: true };
  }
  const preflightNotice = buildProtectedConstraintsPreflightNotice(details, diagnostics);
  if (preflightNotice) return preflightNotice;
  const capacityGuard = diagnostics?.protected_order_capacity_guard;
  const operationalGuard = diagnostics?.final_operational_guard?.status === "final_payload_rejected"
    ? diagnostics.final_operational_guard : null;
  const reason = details?.reason_code || operationalGuard?.reason_code;
  const capacityViolations = reason === "capacity_exceeded"
    ? details?.violations
    : capacityGuard?.status === "preflight_rejected" ? capacityGuard.violations : null;
  const unchangedPlanning = "Aucune modification n’a été sauvegardée. Le planning précédent est conservé.";
  const formatTons = (value) => `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2 }).format(value)} t`;

  if (Array.isArray(capacityViolations) && capacityViolations.length
    && capacityViolations.every((item) => item?.order_id && (item.truck_name || item.truck_id)
      && Number.isFinite(item.order_quantity_tons) && Number.isFinite(item.capacity_tons)
      && item.order_quantity_tons > item.capacity_tons)) {
    return {
      title: "Optimisation bloquée — capacité insuffisante",
      statusLabel: "Action requise",
      message: `Les commandes protégées signalées dépassent la capacité actuelle de leur remorque ou de leur dompeur. Le contrôle de capacité bloque ces affectations. ${unchangedPlanning}`,
      detailItems: [{
        key: "next-step",
        label: "Pour relancer",
        value: "Retirez la protection des commandes signalées pour permettre leur réaffectation, puis relancez Optimiser. Vous pouvez aussi les réaffecter manuellement, ou corriger la capacité de la remorque (Data Manager › Remorques ; « Camions » pour un dompeur) si elle est fausse.",
      }],
      issueItems: capacityViolations.map((violation, index) => ({
        key: `protected-capacity-${index}`,
        orderId: violation.order_id,
        title: `Camion ${violation.truck_name || violation.truck_id} — capacité insuffisante`,
        summary: `${formatTons(violation.order_quantity_tons)} à transporter pour une capacité de ${formatTons(violation.capacity_tons)}.`,
      })),
    };
  }

  const protectedRouteConflicts = details?.same_od_route_failure_protected_conflicts
    || diagnostics?.post_route_max_work_repair?.same_od_route_failure_protected_conflicts;
  const hasProtectedRouteConflict = Array.isArray(protectedRouteConflicts) && protectedRouteConflicts.length > 0;
  if (reason !== "max_work_exceeded" && !hasProtectedRouteConflict) {
    return null;
  }
  const truckViolations = hasProtectedRouteConflict
    ? [...new Map(protectedRouteConflicts.flatMap((conflict) => conflict.truck_violations || [])
      .filter((item) => item?.truck_id)
      .map((item) => [item.truck_id, item])).values()]
    : [];
  const violation = truckViolations[0] || details?.violation || operationalGuard?.violation || {};
  const truckLabel = normalizeInlineText(violation.truck_name) || normalizeInlineText(violation.truck_id);
  const detailItems = [];
  if (Number.isFinite(violation.duration_minutes)) {
    detailItems.push({ key: "duration", label: `Durée calculée${truckViolations.length > 1 ? ` — ${truckLabel}` : ""}`, value: formatDiagnosticDurationLabel(violation.duration_minutes * 60000) });
  }
  if (Number.isFinite(violation.limit_minutes)) {
    const hasLimitBreakdown = Number.isFinite(violation.max_work_hours) && Number.isFinite(violation.max_work_flex_minutes);
    const breakdown = hasLimitBreakdown
      ? ` (${formatDiagnosticDurationLabel(violation.max_work_hours * 3600000)} maximum${violation.max_work_flex_minutes > 0 ? ` + ${formatDiagnosticDurationLabel(violation.max_work_flex_minutes * 60000)} de tolérance` : ", sans tolérance"})`
      : "";
    detailItems.push({ key: "work-limit", label: `Limite appliquée${truckViolations.length > 1 ? ` — ${truckLabel}` : ""}`, value: `${formatDiagnosticDurationLabel(violation.limit_minutes * 60000)}${breakdown}` });
  }
  detailItems.push({
    key: "next-step",
    label: "Pour relancer",
    value: "Vérifiez les affectations et la durée maximale dans le Data Manager. Si des commandes protégées peuvent changer de camion, retirez leur protection ou réaffectez-les manuellement, puis relancez Optimiser. Corrigez les heures max (Data Manager, onglet « Chauffeurs ») seulement si elles sont fausses.",
  });
  return {
    title: "Optimisation bloquée — durée maximale dépassée",
    statusLabel: "Action requise",
    message: `Le résultat recalculé dépasse la durée autorisée${truckViolations.length > 1 ? ` pour les camions ${truckViolations.map((item) => item.truck_name || item.truck_id).join(", ")}` : truckLabel ? ` pour le camion ${truckLabel}` : " pour un camion"}. ${hasProtectedRouteConflict ? "Certaines commandes doivent rester sur leur camion car elles sont protégées, déjà chargées ou verrouillées. " : ""}Le contrôle horaire a empêché sa sauvegarde. ${unchangedPlanning}`,
    detailItems,
    ...(hasProtectedRouteConflict ? {
      issueItems: [
        ...(truckViolations.length > 1 ? truckViolations.map((item) => ({
          key: `work-limit-${item.truck_id}`,
          title: `Camion ${item.truck_name || item.truck_id}`,
          summary: `${formatDiagnosticDurationLabel(item.duration_minutes * 60000)} calculées pour ${formatDiagnosticDurationLabel(item.limit_minutes * 60000)} autorisées, tolérance comprise.`,
        })) : []),
        ...protectedRouteConflicts.map((conflict, index) => ({
          key: `protected-route-${index}`,
          title: `Commandes à vérifier${conflict.client_names?.length ? ` — ${conflict.client_names.join(", ")}` : ""}`,
          summary: (conflict.immutable_order_ids || []).join(", "),
        })),
      ],
    } : {}),
  };
}

function buildOptimizationProtectedAssignmentNoticeContent(jobState) {
  if (jobState?.operationKind === "planning_reset" || jobState?.resultCommitted === true) {
    return null;
  }
  const diagnostics = resolveOptimizationDiagnostics(jobState?.optimizationDiagnostics);
  const guard = [jobState?.errorDetails, diagnostics?.protected_membership_guard].find((candidate) => [
    "protected_membership_requires_recalculation",
    "protected_membership_fallback_missing",
    "protected_membership_fallback_invalid",
    "protected_membership_fallback_hard_time_window_invalid",
  ].includes(candidate?.reason_code));
  if (!guard) return null;

  const assignments = diagnostics?.solver_breakdown?.post_optimizer_solution?.assignments;
  const truckNames = new Map((Array.isArray(assignments) ? assignments : [])
    .map((assignment) => [assignment?.truck_id, normalizeInlineText(assignment?.truck_name)]));
  const violations = (Array.isArray(guard.violations) ? guard.violations : [])
    .filter((item) => normalizeInlineText(item?.order_id) && normalizeInlineText(item?.expected_truck_id));
  const ordersByTruck = new Map();
  for (const violation of violations) {
    const truckId = violation.expected_truck_id;
    const group = ordersByTruck.get(truckId) || {
      truckLabel: normalizeInlineText(violation.expected_truck_name) || truckNames.get(truckId) || truckId,
      orderIds: new Set(),
    };
    group.orderIds.add(violation.order_id);
    ordersByTruck.set(truckId, group);
  }
  const allUnassigned = violations.length > 0
    && violations.every((item) => ["listed_unassigned", "missing_assignment"].includes(item.reason_code));
  return {
    title: "Optimisation non sauvegardée",
    message: `${allUnassigned
      ? "Le calcul a laissé des commandes protégées sans camion."
      : "Le calcul n’a pas conservé certaines commandes sur leur camion protégé."} Le planning précédent est conservé.`,
    detailItems: ordersByTruck.size ? [...ordersByTruck].map(([truckId, group]) => ({
      key: `protected-assignments-${truckId}`,
      label: "Commandes à vérifier",
      value: `${[...group.orderIds].join(", ")} — camion ${group.truckLabel}`,
    })) : [{
      key: "next-step",
      label: "À vérifier",
      value: "Vérifiez les affectations protégées dans le dispatch avant de relancer.",
    }],
  };
}

function buildOptimizationFailureNoticeContent(jobState) {
  const constraintNotice = buildOptimizationConstraintNoticeContent(jobState);
  if (constraintNotice) return constraintNotice;
  const protectedAssignmentNotice = buildOptimizationProtectedAssignmentNoticeContent(jobState);
  if (protectedAssignmentNotice) return protectedAssignmentNotice;
  const rawError = normalizeInlineText(jobState?.error || jobState?.message);
  const structuredDetailItems = Array.isArray(jobState?.detailItems) ? jobState.detailItems : [];
  const structuredIssueItems = Array.isArray(jobState?.issueItems) ? jobState.issueItems : [];
  if (structuredDetailItems.length || structuredIssueItems.length) {
    return {
      detailItems: structuredDetailItems,
      issueItems: structuredIssueItems,
      message: rawError || "Une action explicite est requise avant de relancer l'optimisation.",
    };
  }

  return buildOptimizationValidationNoticeContent(rawError);
}

function buildOptimizationCompletionNoticeContent(jobState) {
  return buildOptimizationSolverBudgetNoticeContent(jobState);
}

function normalizeStatusNoticeServiceActivityItems(candidate) {
  const items = (Array.isArray(candidate) ? candidate : []).reduce((items, item) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const key = normalizeInlineText(item.key);
    const label = normalizeInlineText(item.label);
    const value = normalizeInlineText(item.value);
    if (!key || !label || !value) {
      return items;
    }

    const description = normalizeInlineText(item.description);
    const example = normalizeInlineText(item.example);
    const examplesTitle = normalizeInlineText(item.examplesTitle);
    const exampleRecords = (Array.isArray(item.exampleRecords) ? item.exampleRecords : [])
      .reduce((entries, entry) => {
        if (!entry || typeof entry !== "object") {
          return entries;
        }

        const label = normalizeInlineText(entry.label);
        const detail = normalizeInlineText(entry.detail);
        if (!label) {
          return entries;
        }

        entries.push({
          ...(detail ? { detail } : {}),
          label,
        });
        return entries;
      }, [])
      .slice(0, 10);
    const examples = (Array.isArray(item.examples) ? item.examples : [])
      .filter((entry) => typeof entry === "string" && entry.trim())
      .map((entry) => entry.trim())
      .slice(0, 10);

    items.push({
      ...(description ? { description } : {}),
      ...(example ? { example } : {}),
      ...(examplesTitle ? { examplesTitle } : {}),
      ...(exampleRecords.length ? { exampleRecords } : {}),
      ...(examples.length ? { examples } : {}),
      key,
      label,
      value,
      tone: normalizeInlineText(item.tone) || "neutral",
    });
    return items;
  }, []);

  return items;
}

function normalizeStatusNoticeDetailItems(candidate) {
  return (Array.isArray(candidate) ? candidate : []).reduce((items, item, index) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const label = normalizeInlineText(item.label);
    const value = normalizeInlineText(item.value);
    if (!label || !value) {
      return items;
    }

    items.push({
      key: normalizeInlineText(item.key) || `detail-${index + 1}`,
      label,
      value,
    });
    return items;
  }, []);
}

function normalizeStatusNoticeIssueItems(candidate) {
  return (Array.isArray(candidate) ? candidate : []).reduce((items, item, index) => {
    if (!item || typeof item !== "object") {
      return items;
    }

    const title = normalizeInlineText(item.title);
    if (!title) {
      return items;
    }

    const values = buildOptimizationValidationIssueValues(item.values || []);

    items.push({
      key: normalizeInlineText(item.key) || `issue-${index + 1}`,
      orderId: normalizeInlineText(item.orderId || item.order_id),
      summary: normalizeInlineText(item.summary),
      title,
      values,
    });
    return items;
  }, []);
}

function resolveStatusNoticeDiagnosticsCandidate(candidate) {
  const planningDiagnostics = candidate?.planningDiagnostics
    || candidate?.planning_diagnostics
    || candidate?._planning_diagnostics
    || null;
  const optimizationDiagnostics = candidate?.optimizationDiagnostics
    || candidate?.optimization_diagnostics
    || null;
  const genericDiagnostics = candidate?.diagnostics || null;

  return planningDiagnostics || optimizationDiagnostics || genericDiagnostics || null;
}

function resolveStatusNoticeExecutionSummaryDiagnostics(candidate, source) {
  if (source === "planning") {
    return null;
  }

  return resolveStatusNoticeDiagnosticsCandidate(candidate);
}

function resolveStatusNoticeBackendTotalLabel(candidate) {
  const diagnostics = resolveStatusNoticeDiagnosticsCandidate(candidate);
  if (!diagnostics || typeof diagnostics !== "object" || Array.isArray(diagnostics)) {
    return null;
  }

  const totalBackendMs = Number(diagnostics.total_backend_ms ?? diagnostics.totalBackendMs);
  if (!Number.isFinite(totalBackendMs) || totalBackendMs < 0) {
    return null;
  }

  return formatDiagnosticDurationLabel(totalBackendMs);
}

function buildNormalizedStatusNoticeServiceActivityItems(candidate, source) {
  return normalizeStatusNoticeServiceActivityItems(buildStatusNoticeExecutionSummaryItems({
    allowServiceHealthFallback: source !== "planning",
    context: candidate?.executionSummaryContext || null,
    diagnostics: resolveStatusNoticeExecutionSummaryDiagnostics(candidate, source),
    serviceHealth: candidate?.serviceHealth || candidate?.service_health || null,
  }));
}

export function normalizeStatusNotice(candidate) {
  if (!candidate || typeof candidate !== "object" || candidate.isDismissed) {
    return null;
  }

  const status = typeof candidate.status === "string"
    ? candidate.status.trim().toLowerCase()
    : "idle";

  if (!["running", "cancelling", "cancelled", "completed", "failed"].includes(status)) {
    return null;
  }

  const source = typeof candidate.source === "string" && candidate.source.trim() ? candidate.source : "optimization";
  const detailItems = normalizeStatusNoticeDetailItems(candidate.detailItems);
  const issueItems = normalizeStatusNoticeIssueItems(candidate.issueItems);
  const serviceActivityItems = buildNormalizedStatusNoticeServiceActivityItems(candidate, source);
  const backendTotalLabel = resolveStatusNoticeBackendTotalLabel(candidate);
  const captureContext = buildStatusNoticeAlertCaptureContext({
    ...candidate,
    detailItems,
    issueItems,
    source,
    status,
  });

  return {
    backendTotalLabel,
    captureContext,
    eyebrow: typeof candidate.eyebrow === "string" && candidate.eyebrow.trim()
      ? candidate.eyebrow
      : (status === "running"
        ? "Suivi optimisation"
        : status === "cancelling"
          ? "Annulation en cours"
          : status === "completed"
            ? "Mise à jour disponible"
            : status === "cancelled"
              ? "Optimisation annulée"
              : "Action requise"),
    finishedAt: typeof candidate.finishedAt === "string" && candidate.finishedAt.trim() ? candidate.finishedAt : null,
    message: typeof candidate.message === "string" ? candidate.message : "",
    progress: sanitizeOptimizationProgressForStatus(candidate.progress, status),
    resultActionLabel: typeof candidate.resultActionLabel === "string" && candidate.resultActionLabel.trim()
      ? candidate.resultActionLabel
      : "Voir les résultats",
    resultUrl: typeof candidate.resultUrl === "string" && candidate.resultUrl.trim() ? candidate.resultUrl : null,
    source,
    startedAt: typeof candidate.startedAt === "string" && candidate.startedAt.trim() ? candidate.startedAt : null,
    status,
    statusLabel: typeof candidate.statusLabel === "string" && candidate.statusLabel.trim()
      ? candidate.statusLabel
      : (status === "running"
        ? "Calcul en cours"
        : status === "cancelling"
          ? "Annulation demandée"
          : status === "completed"
            ? "Prêt à ouvrir"
            : status === "cancelled"
              ? "Annulée"
              : "Échec"),
    detailItems,
    issueItems,
    serviceActivityLabel: typeof candidate.serviceActivityLabel === "string" && candidate.serviceActivityLabel.trim()
      ? candidate.serviceActivityLabel.trim()
      : (source === "planning" ? "Dernier run complet" : "Source / exécution"),
    serviceActivityItems,
    title: typeof candidate.title === "string" && candidate.title.trim()
      ? candidate.title
      : (status === "running"
        ? "Mise à jour en cours"
        : status === "cancelling"
          ? "Arrêt de l'optimisation en cours"
          : status === "completed"
            ? "Mise à jour terminée"
            : status === "cancelled"
              ? "Optimisation annulée"
              : "Modification du planning interrompue"),
  };
}

export function isPersistentStatusNotice(notice) {
  if (!notice) {
    return false;
  }

  if (notice.status !== "running") {
    return true;
  }

  return notice.source === "results_export" || notice.source === "import";
}

export function buildOptimizationStatusNotice(jobState) {
  if (!jobState || jobState.status === "idle" || jobState.isDismissed) {
    return null;
  }

  const finishedAtLabel = formatOptimizationStatusTime(jobState.finishedAt);
  const isRunning = jobState.status === "running";
  const isCancelling = jobState.status === "cancelling";
  const isCancelled = jobState.status === "cancelled";
  const isCompleted = jobState.status === "completed";
  const isPlanningReset = jobState.operationKind === "planning_reset";
  const completionNoticeContent = isCompleted ? buildOptimizationCompletionNoticeContent(jobState) : null;
  const failureNoticeContent = isCompleted || isRunning || isCancelling || isCancelled ? null : buildOptimizationFailureNoticeContent(jobState);
  const existingCaptureContext = jobState.captureContext
    && typeof jobState.captureContext === "object"
    && !Array.isArray(jobState.captureContext)
    ? jobState.captureContext
    : {};
  const operationId = jobState.jobId || existingCaptureContext.operation_id || "";
  const resolvedDispatchDate = jobState.dispatchDate
    || existingCaptureContext.dispatch?.target_date
    || readCurrentDispatchDate();
  const resolvedDispatchPeriod = jobState.dispatchPeriod
    || existingCaptureContext.dispatch?.target_period
    || readCurrentDispatchPeriod();
  const baseTitle = isPlanningReset
    ? isCompleted
      ? "Réinitialisation du planning terminée"
      : isRunning
        ? "Réinitialisation en cours"
        : isCancelling
          ? "Arrêt de la réinitialisation en cours"
          : isCancelled
            ? "Réinitialisation du planning annulée"
            : "Réinitialisation du planning interrompue"
    : isCompleted
      ? "Optimisation de la flotte terminée"
      : isRunning
        ? "Optimisation en cours"
        : isCancelling
          ? "Arrêt de l'optimisation en cours"
          : isCancelled
            ? "Optimisation de la flotte annulée"
            : "Optimisation de la flotte interrompue";

  return normalizeStatusNotice({
    action: jobState.operationKind,
    actionLabel: isPlanningReset ? "Réinitialiser" : "Optimiser",
    captureContext: {
      ...existingCaptureContext,
      action: jobState.operationKind,
      action_label: isPlanningReset ? "Réinitialiser" : "Optimiser",
      dispatch: {
        ...(existingCaptureContext.dispatch || {}),
        target_date: resolvedDispatchDate,
        target_period: resolvedDispatchPeriod,
      },
      interaction_origin: "direct",
      operation_id: operationId,
      source: "optimization",
      technical: {
        ...(existingCaptureContext.technical || {}),
        correlation_id: existingCaptureContext.technical?.correlation_id || operationId,
        reason_code: jobState.errorDetails?.reason_code || existingCaptureContext.technical?.reason_code,
        method: existingCaptureContext.technical?.method || "POST",
        path: existingCaptureContext.technical?.path
          || (isPlanningReset ? "/api/planning/reset" : "/api/optimize"),
      },
    },
    dispatchDate: resolvedDispatchDate,
    dispatchPeriod: resolvedDispatchPeriod,
    eyebrow: isPlanningReset ? "Planning manuel" : "Optimisation de la flotte",
    finishedAt: jobState.finishedAt,
    message: isCompleted
      ? (isPlanningReset
        ? (jobState.message || `La réinitialisation du planning est terminée${finishedAtLabel ? ` depuis ${finishedAtLabel}` : ""}.`)
        : (completionNoticeContent?.message || `L'optimisation de la flotte est terminée${finishedAtLabel ? ` depuis ${finishedAtLabel}` : ""}.`))
      : isRunning
        ? (jobState.message || (isPlanningReset ? "La réinitialisation du planning est en cours." : "L'optimisation de la flotte est en cours."))
        : isCancelling
          ? (jobState.message || "Annulation demandée. Le calcul s'arrête après la phase en cours.")
          : isCancelled
            ? (jobState.message || (isPlanningReset
              ? "La réinitialisation a été annulée. Le planning précédent reste disponible."
              : "L'optimisation a été annulée. Les résultats précédents restent affichés."))
        : (isPlanningReset
          ? ((failureNoticeContent?.replacesError && failureNoticeContent.message) || jobState.error || jobState.message || failureNoticeContent?.message || "La réinitialisation du planning n'a pas pu aller au bout.")
          : (failureNoticeContent?.message || jobState.error || jobState.message || "Le calcul n'a pas pu aller au bout.")),
    detailItems: completionNoticeContent?.detailItems || failureNoticeContent?.detailItems,
    issueItems: failureNoticeContent?.issueItems,
    resultActionLabel: isPlanningReset
      ? "Voir le planning"
      : isCancelled ? "Voir les résultats actuels" : "Voir les résultats",
    resultUrl: jobState.resultUrl,
    optimizationDiagnostics: jobState.optimizationDiagnostics,
    operationId,
    progress: jobState.progress,
    serviceHealth: jobState.serviceHealth,
    source: "optimization",
    startedAt: jobState.startedAt,
    status: jobState.status,
    statusLabel: isCompleted
      ? "Prêt à ouvrir"
      : isCancelling
        ? "Annulation demandée"
        : isCancelled
          ? "Annulée"
          : isRunning
            ? "Calcul en cours"
            : (failureNoticeContent?.statusLabel || "Échec"),
    title: appendOptimizationDispatchLabel(failureNoticeContent?.title || baseTitle, resolvedDispatchDate, resolvedDispatchPeriod),
  });
}
